/**
 * Eine Ortung über `navigator.geolocation` (27.10.2026) — hohe Genauigkeit,
 * Zeitlimit, kein zwischengespeicherter Punkt. Nur im Vordergrund: Im
 * Hintergrund gibt iOS einer Web-App keinen Standort.
 *
 * Kein Rückfall auf IP-Ortung: im Mobilfunk oft über 100 km daneben, und der
 * Ort ginge an einen fremden Dienst.
 */
import { GEO_TIMEOUT_MS } from '@kraftwerk/shared';
import type { Fix } from '../db/location.js';

export type GeoFailure = 'unsupported' | 'denied' | 'timeout' | 'unavailable';

export class GeoError extends Error {
  constructor(readonly reason: GeoFailure) {
    super(`Ortung: ${reason}`);
    this.name = 'GeoError';
  }
}

export function geolocate(timeoutMs: number = GEO_TIMEOUT_MS): Promise<Fix> {
  const geo = typeof navigator === 'undefined' ? undefined : navigator.geolocation;
  if (!geo) return Promise.reject(new GeoError('unsupported'));
  return new Promise((resolve, reject) => {
    geo.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyM: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null,
          at: new Date(position.timestamp || Date.now()).toISOString(),
        }),
      (error) =>
        reject(
          new GeoError(error.code === 1 ? 'denied' : error.code === 3 ? 'timeout' : 'unavailable'),
        ),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}
